import { create } from 'zustand';
import { ICommentAPI } from '../components/organism/comment';
import { useUserStore } from '../../modules/Auth/stores/authStore';

interface ICommentState {
  comments: Array<ICommentAPI>;
  allComments: Array<ICommentAPI> | null;
  setComments: (data: any, isFirstPage?: boolean) => void;
  addCommentState: (data: any) => void;
  deleteCommentState: (comment: any) => void;
  upvoteCommentState: (data: any) => void;
  updateCommentState: (id: string, newComment: string) => void;
  showAllComments: () => void;
  totalComments: number;
  totalShowAll: number;
  enableShowAll: boolean;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  setPaginationInfo: (totalComments: number, totalPages: number) => void;
  resetComments: () => void;
}

export const useCommentsStore = create<ICommentState>((set, get) => ({
  comments: [],
  totalComments: 0,
  totalShowAll: 0,
  allComments: [],
  enableShowAll: true,
  currentPage: 1,
  totalPages: 1,
  resetComments: () => {
    set({
      comments: [],
      allComments: [],
      totalComments: 0,
      totalShowAll: 0,
      enableShowAll: true,
      currentPage: 1,
      totalPages: 1,
    });
  },
  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },
  setPaginationInfo: (totalComments: number, totalPages: number) => {
    const { currentPage } = get();
    set({
      totalComments,
      totalPages,
      enableShowAll: currentPage < totalPages,
      totalShowAll: totalComments - get().comments.length,
    });
  },
  showAllComments: () => {
    set({
      comments: get().allComments || [],
      enableShowAll: false,
    });
  },
  setComments: (data, isFirstPage = true) => {
    const userId = useUserStore.getState().user?.id;

    const normalizeComment = (comment: any) => {
      const likes = comment.likes || [];
      return {
        ...comment,
        qtdLikes: likes.length,
        isLiked: likes.some(
          (like: any) =>
            like === userId || like?.id === userId || like?._id === userId
        ),
        children: (comment.children || []).map(normalizeComment),
      };
    };

    const normalizedData = data.map(normalizeComment);
    const currentComments = get().comments;

    if (isFirstPage) {
      set({
        comments: normalizedData,
        allComments: normalizedData,
      });
    } else {
      const mergedComments = [...currentComments, ...normalizedData];
      set({
        comments: mergedComments,
        allComments: mergedComments,
        totalShowAll: get().totalComments - mergedComments.length,
        enableShowAll: get().currentPage < get().totalPages,
      });
    }
  },
  addCommentState: (comment) => {
    const { allComments, comments, totalComments, totalShowAll } = get();

    const normalizedComment = {
      ...comment,
      qtdLikes: comment.qtdLikes ?? 0,
      isLiked: comment.isLiked ?? false,
      likes: comment.likes || [],
    };

    if (comment.parent) {
      const newAllComments = (allComments || []).map((item) =>
        item._id === comment.parent
          ? { ...item, children: [normalizedComment, ...(item.children || [])] }
          : item
      );
      const newComments = (comments || []).map((item) =>
        item._id === comment.parent
          ? { ...item, children: [normalizedComment, ...(item.children || [])] }
          : item
      );

      set({
        comments: newComments,
        allComments: newAllComments,
      });
    } else {
      const newComments = [normalizedComment, ...(comments || [])];
      const newTotalComments = totalComments + 1;
      set({
        comments: newComments,
        allComments: [normalizedComment, ...(allComments || [])],
        totalComments: newTotalComments,
        totalShowAll: newTotalComments - newComments.length,
      });
    }
  },
  deleteCommentState: (comment) => {
    const { allComments, comments, totalComments, currentPage, totalPages } = get();
    const commentId = comment.id || comment._id;
    if (comment.parent) {
      const newAllComments = (allComments || []).map((item) =>
        item._id === comment.parent
          ? {
              ...item,
              children: (item.children || []).filter(
                (child) => (child.id || child._id) !== commentId
              ),
            }
          : item
      );
      const newComments = (comments || []).map((item) =>
        item._id === comment.parent
          ? {
              ...item,
              children: (item.children || []).filter(
                (child) => (child.id || child._id) !== commentId
              ),
            }
          : item
      );

      set({
        comments: newComments,
        allComments: newAllComments,
      });
    } else {
      const newAllComments = (allComments || []).filter(
        (item) => (item._id || item.id) !== commentId
      );
      const newComments = (comments || []).filter(
        (item) => (item._id || item.id) !== commentId
      );
      const newTotalComments = totalComments - 1;
      set({
        comments: newComments,
        allComments: newAllComments,
        totalComments: newTotalComments,
        totalShowAll: newTotalComments - newComments.length,
        enableShowAll: currentPage < totalPages,
      });
    }
  },
  upvoteCommentState: (data) => {
    function updateComment(comment: ICommentAPI) {
      if ((comment._id || comment.id) === data.id) {
        return {
          ...comment,
          likes: data.likes,
          qtdLikes: data.likes.length,
          isLiked: data.like,
        };
      }

      const newChildren: any[] = (comment.children || []).map(updateComment);
      return { ...comment, children: newChildren };
    }

    const allComments = (get().allComments || []).map(updateComment);
    const comments = (get().comments || []).map(updateComment);

    set({ comments, allComments });
  },
  updateCommentState: (id: string, newComment: string) => {
    const { allComments, comments } = get();

    const updateComment = (item: ICommentAPI) => {
      const updatedItem =
        (item._id || item.id) === id
          ? { ...item, text: newComment, updatedAt: new Date() }
          : item;

      return {
        ...updatedItem,
        children: (updatedItem.children || []).map((child) =>
          (child._id || child.id) === id
            ? { ...child, text: newComment, updatedAt: new Date() }
            : child
        ),
      };
    };

    const newComments = (comments || []).map(updateComment);
    const newAllComments = (allComments || []).map(updateComment);

    set({ comments: newComments, allComments: newAllComments });
  },
}));
